// Claude: Admin Companies page — aggregated read-only view of sellers + buyers by company name 01-03-2026
import React, { useEffect, useState } from "react";
import { Table, Input, Select, Tag } from "antd";
import { Box, Text } from "@chakra-ui/react";
import { AdminAPI } from "../../Apis/adminAPI";
import { useToastWrapper } from "../../Wrapper/toastWrapper";
import { errorHandler } from "../../Utils/handler";

const { Search } = Input;
const { Option } = Select;

type CompanyRow = {
  companyName: string;
  type: "seller" | "buyer" | "both";
};

const TYPE_COLORS: Record<string, string> = {
  seller: "blue",
  buyer: "green",
  both: "purple",
};

const Companies = () => {
  const [data, setData] = useState<CompanyRow[]>([]);
  const [loading, setLoading] = useState(false);
  const [search, setSearch] = useState("");
  const [typeFilter, setTypeFilter] = useState<string>("all");
  const { error } = useToastWrapper();

  useEffect(() => {
    const fetch = async () => {
      setLoading(true);
      try {
        const res = await AdminAPI.getCompanies();
        setData(Array.isArray(res) ? res : (res?.data ?? []));
      } catch (err: any) {
        error(errorHandler(err));
      } finally {
        setLoading(false);
      }
    };
    fetch();
  }, []);

  // Claude: counts per type for dropdown labels 01-03-2026
  const counts = {
    all: data.length,
    seller: data.filter((r) => r.type === "seller").length,
    buyer: data.filter((r) => r.type === "buyer").length,
    both: data.filter((r) => r.type === "both").length,
  };

  const filtered = data.filter((row) => {
    const matchesSearch = row.companyName.toLowerCase().includes(search.toLowerCase());
    const matchesType = typeFilter === "all" || row.type === typeFilter;
    return matchesSearch && matchesType;
  });

  const columns = [
    {
      title: "Company Name",
      dataIndex: "companyName",
      key: "companyName",
      sorter: (a: CompanyRow, b: CompanyRow) => a.companyName.localeCompare(b.companyName),
    },
    {
      title: "Type",
      dataIndex: "type",
      key: "type",
      sorter: (a: CompanyRow, b: CompanyRow) => a.type.localeCompare(b.type),
      render: (type: string) => (
        <Tag color={TYPE_COLORS[type]} style={{ textTransform: "capitalize" }}>
          {type}
        </Tag>
      ),
    },
  ];

  return (
    <div className="p-4">
      <Text fontSize="2xl" fontWeight={600} mb={4}>
        Companies <Text as="span" fontSize="lg" fontWeight={400} color="gray.500">({counts.all})</Text>
      </Text>

      <Box display="flex" gap={3} mb={4}>
        <Search
          placeholder="Search company name"
          allowClear
          onChange={(e) => setSearch(e.target.value)}
          style={{ width: 280 }}
        />
        <Select
          value={typeFilter}
          onChange={(val) => setTypeFilter(val)}
          style={{ width: 160 }}
        >
          <Option value="all">All ({counts.all})</Option>
          <Option value="seller">Seller ({counts.seller})</Option>
          <Option value="buyer">Buyer ({counts.buyer})</Option>
          <Option value="both">Both ({counts.both})</Option>
        </Select>
      </Box>

      <Table
        columns={columns}
        dataSource={filtered}
        rowKey="companyName"
        loading={loading}
        pagination={{ pageSize: 20, showSizeChanger: false }}
        size="middle"
      />
    </div>
  );
};

export default Companies;
